import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { faBolt } from '@fortawesome/pro-regular-svg-icons/faBolt';
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons/faFileAlt';
import { faStethoscope } from '@fortawesome/pro-regular-svg-icons/faStethoscope';
import { PROD_MEDIA_SERVER } from '@osmosis/constants';
import {
  FlashcardSection,
  NotesSection,
  QuestionSection,
  UpNext,
  UserAssessmentProgress,
} from '@osmosis/shared/types/learndb/learnData';

import VideoNextUpItem from './VideoNextUpItem';
import NextUpCard from './NextUpCard';

import { useQueueContext } from '../../context/QueueProvider';
import { usePagesContext } from '../../context/PagesProvider';
import { flattenQueues } from '../../lib/shared/quiz/queue';

type Props = {
  video?: UpNext;
  questions?: QuestionSection;
  flashcards?: FlashcardSection;
  notes?: NotesSection;
  onVideoClick?: () => void;
  assessmentProgress?: UserAssessmentProgress;
};

const HeaderText = ({ children }: { children: React.ReactElement }) => (
  <H4 className={`hidden md:block ${text0} mb-2`}>{children}</H4>
);

/**
 * Next up video overlay.
 */
const VideoNextUp = (props: Props) => {
  const { video, notes, flashcards, onVideoClick, assessmentProgress } = props;
  const { questions = {} } = assessmentProgress || {};
  const [notesPage] = notes?.pages || [];

  const intl = useIntl();
  const { push, params } = usePagesContext();

  const { saveToQueue, queues } = useQueueContext();
  const result = Object.entries(questions).find(
    ([key, value]) => value.available > value.attempted,
  );
  let questionTag: string, questionValue;
  if (result) {
    [questionTag, questionValue] = result;
  }
  const availableQuestions = questionValue?.available;

  const flashcardsProgress = assessmentProgress?.flashcards;
  const flashcardQueueItems = Object.values(
    queues.flashcards?.index
      ? flattenQueues(queues.flashcards?.index)
      : { done: [], todo: [] },
  ).reduce((arr, current) => [...arr, ...current], []);

  const addedToQueue = flashcards?.list.every((nodeID) =>
    flashcardQueueItems.find((n) => n === nodeID),
  );

  const hasFlashcards = !!flashcardsProgress?.available;
  const hasQuestions = !!questionValue;
  const hasNotes = !!notesPage;
  const hasVideo = !!video;

  const handleStartQuestions = () => {
    const [learnPage] = params.concept;

    push(`/learn-quiz/${learnPage}/${questionTag}/questions`);
  };

  const handleAddToQueue = () => {
    if (flashcards) saveToQueue(flashcards.list, 'flashcards');
  };

  const handleClickVideo = () => {
    onVideoClick?.();

    if (video?.link) push(video.link);
  };

  const handleClickNotes = () => {
    const notesLink = `/notes/${notes?.notesLink}`;

    window.open(notesLink);
  };

  return (
    <Box>
      <H2
        className={`text-base sm:text-lg md:text-2xl ${text0} mb-3 text-center`}
      >
        <FormattedMessage
          id="learn.video.next_steps.heading"
          defaultMessage="Next steps"
        />
      </H2>

      <Flex className="flex-col gap-2 md:flex-row md:gap-4">
        {hasNotes && (
          <Box>
            <HeaderText>
              <FormattedMessage
                id="learn.video.next_steps.notes"
                defaultMessage="Go deeper on this topic"
              />
            </HeaderText>
            <NextUpCard
              onClick={handleClickNotes}
              icon={faFileAlt}
              name={intl.formatMessage({
                id: 'up_next.high_yield_notes.text',
                defaultMessage: 'High Yield Notes',
              })}
              data-testid="high-yield-notes-card"
              src={notesPage.jpg}
            />
          </Box>
        )}

        {(hasQuestions || hasFlashcards) && (
          <Box className="flex-1">
            <HeaderText>
              <FormattedMessage
                defaultMessage="Test your knowledge"
                id="learn.video.next_steps.test_your_knowledge"
              />
            </HeaderText>

            <Flex className="flex-row gap-2 md:flex-col">
              {hasQuestions && params?.concept?.length && (
                <VideoNextUpItem
                  data-testid="next-up-questions"
                  onClick={handleStartQuestions}
                  heading={intl.formatMessage(
                    {
                      id: 'video.next_up.questions_count',
                      defaultMessage:
                        '{count, plural, one {1 question} other {# questions}}',
                    },
                    {
                      count: availableQuestions,
                    },
                  )}
                  subheading={intl.formatMessage(
                    {
                      id: 'video.next_up.questions_progress_count',
                      defaultMessage: '{attempted} / {available} complete',
                    },
                    {
                      attempted: questionValue?.attempted,
                      available: availableQuestions,
                    },
                  )}
                  icon={faStethoscope}
                />
              )}

              {hasFlashcards && (
                <Box className={addedToQueue ? 'opacity-50' : ''}>
                  <VideoNextUpItem
                    data-testid="next-up-flashcards"
                    onClick={handleAddToQueue}
                    disabled={addedToQueue}
                    heading={intl.formatMessage(
                      {
                        id: 'video.next_up.flashcards_count',
                        defaultMessage:
                          '{count} Flashcard{count, plural, =0 {s} one {} other {s}}',
                      },
                      {
                        count: flashcardsProgress.available,
                      },
                    )}
                    subheading={
                      addedToQueue
                        ? intl.formatMessage({
                            id: 'video.next_up.flashcards_queue_added',
                            defaultMessage: 'Added to queue',
                          })
                        : intl.formatMessage({
                            id: 'video.next_up.flashcards_queue',
                            defaultMessage: 'Add to your queue',
                          })
                    }
                    icon={faBolt}
                  />
                </Box>
              )}
            </Flex>
          </Box>
        )}

        {hasVideo && (
          <Box className="flex-1">
            <HeaderText>
              <FormattedMessage
                defaultMessage="Watch the next topic"
                id="learn.video.next_steps.watch_the_next_topic"
              />
            </HeaderText>

            <NextUpCard
              data-testid="next-up-video"
              onClick={handleClickVideo}
              name={video.title || ''}
              src={
                video?.thumb ? `${PROD_MEDIA_SERVER}${video.thumb}` : undefined
              }
              isVideo={true}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default VideoNextUp;
